import React, { useEffect, useState } from 'react';
import {
	Button,
	Col,
	Row,
	ModalBody,
	Form,
	Spinner,
	Label,
	Input,
	FormFeedback,
} from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import OSelect from '../../../Components/OElements/OSelect';
import { getProductiveCycleStageList, getRegionList, getSpeciesList } from '../../../helpers/establishment/backend_helper';
import { addNewProductiveCycleStageTemplate, updateProductiveCycleStageTemplate, existsProductiveCycleStageTemplate } from '../../../slices/thunks';
import { setRequireCloseModal } from '../../../slices/productiveCycle/reducer';
import { createSelector } from 'reselect';
import OInput from '../../../Components/OElements/OInput';

const ProductiveCycleStageTemplateForm = ({
	dataProductiveCycleStageTemplate,
	isEdit,
	handleToggleModal,
	isOpen,
}) => {
	const dispatch = useDispatch();
	const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);

	const selectorProductiveCycle = createSelector(
		state => state.ProductiveCycle,
		result => ({
			isAdding: result.isAdding,
			isAdded: result.isAdded,
			isUpdating: result.isUpdating,
			isUpdated: result.isUpdated,
			isLoadingList: result.isLoadingList,
			requireCloseModal: result.requireCloseModal,
		})
	);
	const { isAdding, isAdded, isUpdating, isUpdated, isLoadingList, requireCloseModal } = useSelector(selectorProductiveCycle);

	// Formik validation schema
	const validation = useFormik({
		enableReinitialize: true,
		initialValues: {
			species_id: dataProductiveCycleStageTemplate?.species_id || '',
			productive_cycle_stage_id: dataProductiveCycleStageTemplate?.productive_cycle_stage_id || '',
			region_id: dataProductiveCycleStageTemplate?.region_id || '',
			days: dataProductiveCycleStageTemplate?.days || '',
		},
		validationSchema: Yup.object({
			species_id: Yup.string().required('Campo requerido'),
			productive_cycle_stage_id: Yup.string().required('Campo requerido'),
			region_id: Yup.string().required('Campo requerido'),
			days: Yup.number()
				.min(1, 'Debe ser mayor a 0')
				.required('Campo requerido'),
		}),
		onSubmit: async values => {
			try {
				const formData = {
					...values,
					...(isEdit && { _id: dataProductiveCycleStageTemplate._id }),
				};

				let response;
				if (isEdit) {
					response = await dispatch(updateProductiveCycleStageTemplate(formData));
				} else {
					response = await dispatch(addNewProductiveCycleStageTemplate(formData));
				}

				// Manejo de la respuesta
				if (response.meta.requestStatus === 'fulfilled') {
					handleToggleModal();
				}
			} catch (error) {
				console.error('Error al enviar el formulario:', error);
				toast.error('Ocurrió un error al enviar el formulario');
			}
		},
	});

	// Verificar si la combinación ya existe
	useEffect(() => {
		const checkCombination = async () => {
			const { species_id, region_id, productive_cycle_stage_id } = validation.values;

			// Asegurarnos de que los valores estén presentes antes de realizar la solicitud
			if (species_id && region_id && productive_cycle_stage_id) {
				try {
					const response = await dispatch(
						existsProductiveCycleStageTemplate({
							species_id,
							region_id,
							productive_cycle_stage_id,
						})
					);

					// Verificar si la respuesta contiene `exists`
					if (response.payload && response.payload.exists !== undefined) {
						if (response.payload.exists) {
							setIsSubmitDisabled(true);
							toast.info('¡La combinación ya existe! Por favor, selecciona otra.', { autoClose: 3000 });
						} else {
							setIsSubmitDisabled(false);
						}
					} else {
						console.error("La respuesta no contiene la propiedad 'exists':", response);
						setIsSubmitDisabled(false); // Por precaución, permitimos continuar
					}
				} catch (error) {
					console.error('Error verificando la combinación:', error);
					setIsSubmitDisabled(false); // Permitimos continuar si hay un error inesperado
				}
			}
		};

		checkCombination();
	}, [validation.values.species_id, validation.values.region_id, validation.values.productive_cycle_stage_id, dispatch]);


	useEffect(() => {
		if (isOpen) {
			if (requireCloseModal) {
				dispatch(setRequireCloseModal(false));
				handleToggleModal();
			}
		}
	}, [requireCloseModal]);

	return (
		<Form
			className="tablelist-form"
			onSubmit={e => {
				e.preventDefault();
				if (Object.keys(validation.errors).length != 0) { toast.error('Por favor, revisa la información ingresada. Hemos encontrado algunos detalles que necesitan corrección antes de continuar.', { autoClose: 4000 }); }
				validation.handleSubmit();
				return false;
			}}
		>
			<ModalBody className="modal-body">
				<Row className="g-3">
					<Col lg={3}>
						<OSelect
							key="productive_cycle_stage_id"
							label="Etapa del Ciclo Productivo"
							id="productive_cycle_stage_id"
							name="productive_cycle_stage_id"
							isRequired={true}
							value={validation.values.productive_cycle_stage_id}
							requestApi={getProductiveCycleStageList}
							validation={validation}
						/>
					</Col>
					<Col lg={3}>
						<OSelect
							key="species_id"
							label="Especie"
							id="species_id"
							name="species_id"
							isRequired={true}
							value={validation.values.species_id}
							requestApi={getSpeciesList}
							validation={validation}
						/>
					</Col>
					<Col lg={3}>
						<OInput
							name='days'
							label='Cantidad de Días'
							placeholder='Ingrese la cantidad de días'
							type='number'
							validation={validation}
							isRequired={true}
							classNameLabel='form-label'
							classNameInput='form-control'
						/>
					</Col>

					<Col lg={3}>
						<OSelect
							key="region_id"
							label="Región"
							id="region_id"
							name="region_id"
							isRequired={true}
							value={validation.values.region_id}
							requestApi={getRegionList}
							validation={validation}
						/>
					</Col>
				</Row>
			</ModalBody>
			<div className="modal-footer">
				<div className="hstack gap-2 modal-footer-add-btn">
					<Button
						type="button"
						onClick={handleToggleModal}
						className="btn-danger btn-label"
					>
						<i className="icon-x label-icon"></i>
						Cancelar
					</Button>
					<button
						type="submit"
						className="btn btn-label btn-primary"
						disabled={isSubmitDisabled} // Bloquear botón si la combinación ya existe
					>
						{validation.isSubmitting ? (
							<i className="label-icon">
								<Spinner size="sm" />
							</i>
						) : (
							<i className="ri-save-3-line label-icon"></i>
						)}
						{isEdit ? 'Editar' : 'Crear'} Etapa de Ciclo Productivo
					</button>
				</div>
			</div>
		</Form >
	);
};

export default ProductiveCycleStageTemplateForm;
